import { useEffect, useState, useCallback } from "react";

const KEY = "prenotoo-theme";

/** Resolves the active theme: 'light' | 'dark' (resolves 'system' from prefers-color-scheme). */
export function getInitialTheme() {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch { /* noop */ }
  if (typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }
  return "light";
}

function applyTheme(theme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
  try {
    document.querySelector('meta[name="theme-color"]')?.setAttribute(
      "content", theme === "dark" ? "#09090b" : "#ffffff"
    );
  } catch { /* noop */ }
}

// Apply once at module load to avoid FOUC (also handled by inline script in index.html if added).
if (typeof document !== "undefined") {
  applyTheme(getInitialTheme());
}

/** React hook returning [theme, toggle, setTheme]. */
export function useTheme() {
  const [theme, setThemeState] = useState(getInitialTheme);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    try { localStorage.setItem(KEY, next); } catch { /* noop */ }
    applyTheme(next);
    try { window.dispatchEvent(new CustomEvent("theme-changed", { detail: { theme: next } })); } catch { /* noop */ }
  }, []);

  const toggle = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  // Listen to changes from other tabs / windows
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === KEY && (e.newValue === "light" || e.newValue === "dark")) {
        setThemeState(e.newValue);
        applyTheme(e.newValue);
      }
    };
    const onCustom = (e) => {
      if (e.detail?.theme && e.detail.theme !== theme) setThemeState(e.detail.theme);
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("theme-changed", onCustom);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("theme-changed", onCustom);
    };
  }, [theme]);

  return [theme, toggle, setTheme];
}
